import { useCallback } from 'react'
import { useAuth } from 'src/stores/auth'

/**
 * Returns helpers for checking the current user's permissions.
 *
 * Usage:
 *   const { hasPermission, hasPermissionInAnyOrg } = usePermissions()
 *   if (hasPermission('expenses.access')) { ... }
 *
 * Permission codenames follow the '<app_label>.<codename>' convention that the
 * backend encodes in the OrganizationMembership payload, e.g.:
 *   - 'expenses.access'
 *   - 'expenses.create'
 *   - 'receipts.access'
 *   - 'chat.access'
 */
export const usePermissions = () => {
  const activeOrgPermissions = useAuth.use.activeOrgPermissions()
  const user = useAuth.use.user()

  /**
   * Returns true when the user holds the given permission codename in their
   * currently active organisation.
   */
  const hasPermission = useCallback(
    (codename: string): boolean => {
      return activeOrgPermissions.includes(codename)
    },
    [activeOrgPermissions],
  )

  /**
   * Returns true when ANY of the user's org memberships grants the given
   * permission. Useful for nav visibility — show a nav item if the user can
   * access the feature in at least one org.
   */
  const hasPermissionInAnyOrg = useCallback(
    (codename: string): boolean => {
      const memberships = user?.memberships ?? []
      return memberships.some((m) => m.permissions.includes(codename))
    },
    [user],
  )

  /**
   * Returns the list of org IDs whose membership grants the given permission.
   * Useful for filtering org pickers to only show orgs where a feature is available.
   */
  const orgsWithPermission = useCallback(
    (codename: string): string[] => {
      const memberships = user?.memberships ?? []
      return memberships
        .filter((m) => m.permissions.includes(codename))
        .map((m) => m.organizationId)
    },
    [user],
  )

  return {
    hasPermission,
    hasPermissionInAnyOrg,
    orgsWithPermission,
    permissions: activeOrgPermissions,
  }
}
